import { useEffect, useRef, useState } from 'react';
import { WS_BASE_BACKOFF_MS, WS_MAX_BACKOFF_MS } from '@nudge/shared';
import { idb } from '../lib/idb';
import { flushQueue } from '../lib/api';
import { useCardsStore } from '../stores/cards';
import { useCircleStore } from '../stores/circle';
import { useAuthStore } from '../stores/auth';
import { haptics } from '../lib/haptics';
import { sounds } from '../lib/sounds';

const WS_URL = (import.meta.env.VITE_WS_URL as string) || '';

export type WSStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export function useWebSocket(circleId: string | null): WSStatus {
  const [status, setStatus] = useState<WSStatus>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const closedManually = useRef(false);

  useEffect(() => {
    if (!circleId) return;
    closedManually.current = false;

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    async function connect() {
      const token = await idb.getToken();
      if (!token) { setStatus('disconnected'); return; }
      if (cancelled) return;
      setStatus('connecting');
      const url = `${WS_URL}/ws/circle/${circleId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        reconnectAttempt.current = 0;
        // sync state on (re)connect
        if (circleId) {
          void useCardsStore.getState().fetchActive(circleId);
          void flushQueue();
        }
      };

      ws.onmessage = (ev) => {
        try {
          const event = JSON.parse(ev.data as string) as { type: string;[k: string]: unknown };
          if (event.type === '__online_snapshot') {
            useCircleStore.getState().setOnline((event.user_ids as string[]) ?? []);
            return;
          }
          if (event.type === 'member_online' && event.user_id) {
            useCircleStore.getState().markOnline(event.user_id as string);
            return;
          }
          if (event.type === 'member_offline' && event.user_id) {
            useCircleStore.getState().markOffline(event.user_id as string);
            return;
          }
          if (event.type === 'card_sent') {
            useCardsStore.getState().applyEvent(event);
            const me = useAuthStore.getState().user?.id;
            const card = event.card as { sender_id: string; intensity: 'soft' | 'firm' };
            if (card.sender_id !== me) {
              if (card.intensity === 'firm') { haptics.firmReceive(); sounds.firm(); }
              else { haptics.softReceive(); sounds.soft(); }
              if (typeof document !== 'undefined' && document.hidden) {
                const next = useCardsStore.getState().badgeCount + 1;
                useCardsStore.getState().setBadge(next);
              }
            }
            return;
          }
          if (event.type === 'card_cleared' || event.type === 'card_acknowledged') {
            useCardsStore.getState().applyEvent(event);
            return;
          }
        } catch (e) {
          console.warn('[ws] bad message', e);
        }
      };

      ws.onclose = () => {
        setStatus('disconnected');
        if (!closedManually.current && !cancelled) {
          const attempt = reconnectAttempt.current++;
          const backoff = Math.min(WS_MAX_BACKOFF_MS, WS_BASE_BACKOFF_MS * Math.pow(2, attempt));
          reconnectTimer = setTimeout(() => { void connect(); }, backoff);
        }
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    }

    void connect();
    return () => {
      cancelled = true;
      closedManually.current = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };
  }, [circleId]);

  return status;
}
